import React, { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";

export default function ExtendStay({ res, onDone, size = "sm", variant = "outline" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${res.id}/extend`, body);
      toast.success(`${t("extend_ok")} ${fmtDate(data.departure)}`);
      setOpen(false); setDate("");
      onDone?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} data-testid={`extend-${res.id}`}>
        <CalendarPlus className="h-4 w-4 mr-1" />{t("extend_stay")}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="sm:max-w-md bg-background" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarPlus className="h-4 w-4" />{t("extend_stay")} — {res.guest_name} · {res.pitch_label}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t("departure")}: {fmtDate(res.departure)}</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 7].map((d) => (
              <Button key={d} disabled={busy} onClick={() => send({ days: d })} data-testid={`extend-days-${d}`}
                className="flex-1 min-w-[5rem]">+{d} {d === 1 ? t("day") : t("days")}</Button>
            ))}
          </div>
          <div className="space-y-1.5 border-t border-border pt-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("extend_other_date")}</label>
            <Input type="date" value={date} min={res.departure} onChange={(e) => setDate(e.target.value)} data-testid="extend-date" />
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button disabled={busy || !date} onClick={() => send({ departure: date })} data-testid="extend-confirm">{t("extend_stay")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
